import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { PHOTO_ACCEPT, photoErrorMessage, shrinkPhoto } from "../lib/jobPhotos.js";

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const headers = { apikey: anonKey ?? "", authorization: `Bearer ${anonKey ?? ""}` };

async function call(body: FormData | Record<string, unknown>): Promise<{ ok: boolean; data: Record<string, unknown> }> {
  try {
    const isForm = body instanceof FormData;
    const res = await fetch(`${url}/functions/v1/job-photos`, {
      method: "POST",
      headers: isForm ? headers : { ...headers, "content-type": "application/json" },
      body: isForm ? body : JSON.stringify(body),
    });
    return { ok: res.ok, data: (await res.json().catch(() => ({}))) as Record<string, unknown> };
  } catch {
    return { ok: false, data: { error: "network" } };
  }
}

interface Item {
  name: string;
  state: "waiting" | "sending" | "sent" | "failed";
  error?: string;
}

/**
 * The page a customer opens from the text a business sends: pick photos of the job and they go straight to the business. No login: the link's
 * token is the only key, and it expires and allows a limited number of photos (migration 0088, the job-photos Edge Function).
 */
export function PhotoPage() {
  const { token = "" } = useParams();
  const [info, setInfo] = useState<{ business_name: string; job_title: string; remaining: number } | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  async function loadInfo() {
    const { ok, data } = await call({ action: "info", token });
    if (ok) setInfo({ business_name: String(data.business_name ?? ""), job_title: String(data.job_title ?? ""), remaining: Number(data.remaining ?? 0) });
    else setProblem(photoErrorMessage(String(data.error ?? "")));
  }
  useEffect(() => {
    void loadInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function send(files: FileList | null) {
    if (!files || files.length === 0 || busy) return;
    const chosen = [...files].slice(0, info?.remaining ?? 10);
    setBusy(true);
    setItems(chosen.map((f) => ({ name: f.name, state: "waiting" })));
    for (let i = 0; i < chosen.length; i++) {
      const mark = (patch: Partial<Item>) => setItems((all) => all.map((it, j) => (j === i ? { ...it, ...patch } : it)));
      mark({ state: "sending" });
      const form = new FormData();
      form.append("action", "upload");
      form.append("token", token);
      form.append("file", await shrinkPhoto(chosen[i]!));
      const { ok, data } = await call(form);
      if (ok) mark({ state: "sent" });
      else {
        mark({ state: "failed", error: photoErrorMessage(String(data.error ?? "")) });
        if (data.error === "link_full" || data.error === "link_expired") break;
      }
    }
    setBusy(false);
    if (input.current) input.current.value = "";
    await loadInfo();
  }

  const sentCount = items.filter((i) => i.state === "sent").length;
  return (
    <main style={{ maxWidth: 520, margin: "0 auto", padding: "24px 16px", color: "var(--rc-text)", fontFamily: "var(--rc-font)" }}>
      <h1 style={{ fontSize: 22, margin: "0 0 4px" }}>Send photos</h1>
      {info && (
        <p style={{ margin: "0 0 16px", color: "var(--rc-text-muted)", fontSize: 14 }}>
          For {info.business_name}: {info.job_title}
        </p>
      )}
      {problem && !info && <p role="alert" style={{ fontSize: 15 }}>{problem}</p>}
      {info && (
        <>
          <p style={{ fontSize: 14, margin: "0 0 12px" }}>
            Take or choose photos that show the problem. You can send up to {info.remaining} more.
          </p>
          <input ref={input} type="file" accept={PHOTO_ACCEPT} multiple hidden disabled={busy} onChange={(e) => void send(e.target.files)} />
          <button
            className="rc-btn rc-btn-primary"
            disabled={busy || info.remaining < 1}
            onClick={() => input.current?.click()}
            style={{ padding: "12px 20px", fontSize: 16, width: "100%" }}
          >
            {busy ? "Sending..." : "Choose photos"}
          </button>
          {info.remaining < 1 && <p style={{ fontSize: 14, marginTop: 12 }}>{photoErrorMessage("link_full")}</p>}
        </>
      )}
      {items.length > 0 && (
        <ul style={{ listStyle: "none", padding: 0, margin: "16px 0 0", display: "flex", flexDirection: "column", gap: 6, fontSize: 14 }}>
          {items.map((it, i) => (
            <li key={i}>
              {it.state === "sent" ? "Sent" : it.state === "sending" ? "Sending" : it.state === "failed" ? "Not sent" : "Waiting"}: {it.name}
              {it.error && <div style={{ color: "var(--rc-danger)" }}>{it.error}</div>}
            </li>
          ))}
        </ul>
      )}
      {!busy && sentCount > 0 && <p style={{ marginTop: 16, fontSize: 15, color: "var(--rc-success)" }}>Thank you. {sentCount === 1 ? "Your photo has" : `Your ${sentCount} photos have`} been sent.</p>}
    </main>
  );
}
